import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, ChevronRight, Minus, Plus, ShieldCheck, ShoppingBag, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import SEO from "../SEO/SEO";
import { del, patch, post } from "../../utils/request";
import { notifyCartUpdated, useCart } from "./CartContext";
import { displayPrice, itemImage, itemSubtitle } from "./cartDisplay";
import "./cart.scss";

const groupKey = (item) => item?.bundleGroup?.sessionId || item.id;

export default function CartPage() {
  const { items, loading, refreshCart, isAuthenticated } = useCart();
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState([]);
  const [priceInfo, setPriceInfo] = useState(null);
  const [busyId, setBusyId] = useState("");

  useEffect(() => {
    setSelectedIds(items.filter((item) => item.status === "Ready").map((item) => item.id));
  }, [items]);

  useEffect(() => {
    let active = true;
    if (!selectedIds.length) {
      setPriceInfo(null);
      return undefined;
    }
    post("/cart/validate", { cartItemIds: selectedIds })
      .then((result) => active && setPriceInfo(result))
      .catch((error) => active && toast.error(error?.message || "Some cart items need your attention."));
    return () => { active = false; };
  }, [selectedIds]);

  const groups = useMemo(() => {
    const result = [];
    const byKey = new Map();
    items.forEach((item) => {
      const key = groupKey(item);
      if (!byKey.has(key)) {
        const entry = { key, bundle: Boolean(item?.bundleGroup?.sessionId), title: item?.bundleGroup?.title, items: [] };
        byKey.set(key, entry);
        result.push(entry);
      }
      byKey.get(key).items.push(item);
    });
    return result;
  }, [items]);

  const toggleGroup = (group) => {
    const ids = group.items.filter((item) => item.status === "Ready").map((item) => item.id);
    const selected = ids.every((id) => selectedIds.includes(id));
    setSelectedIds((current) => selected
      ? current.filter((id) => !ids.includes(id))
      : [...new Set([...current, ...ids])]);
  };

  const removeGroup = async (group) => {
    setBusyId(group.key);
    try {
      await Promise.all(group.items.map((item) => del(`/cart/items/${item.id}`)));
      await refreshCart();
      notifyCartUpdated();
      toast.success(group.bundle ? "Bundle removed from cart." : "Item removed from cart.");
    } catch (error) {
      toast.error(error?.message || "Unable to remove this item.");
    } finally {
      setBusyId("");
    }
  };

  const updateQuantity = async (item, quantity) => {
    if (quantity < 1) return;
    setBusyId(item.id);
    try {
      await patch(`/cart/items/${item.id}`, { quantity });
      await refreshCart();
      notifyCartUpdated();
    } catch (error) {
      toast.error(error?.message || "Unable to update this quantity.");
    } finally {
      setBusyId("");
    }
  };

  const checkout = () => {
    if (!selectedIds.length) return toast.error("Select at least one item to continue.");
    sessionStorage.setItem("teleport_cart_selection", JSON.stringify(selectedIds));
    navigate("/cart/checkout", { state: { cartItemIds: selectedIds } });
  };

  if (!isAuthenticated()) {
    return (
      <><SEO title="Your Cart | Teleport Now" canonicalPath="/cart" noindex /><Navbar />
        <main className="cart-page cart-page--empty">
          <ShoppingBag aria-hidden="true" />
          <h1>Sign in to open your cart.</h1>
          <p>Your selected travel essentials will stay connected to your Teleport Now account.</p>
          <Link to="/login" state={{ from: "/cart" }}>Sign in</Link>
        </main><Footer /></>
    );
  }

  return (
    <><SEO title="Your Cart | Teleport Now" canonicalPath="/cart" noindex /><Navbar />
      <main className="cart-page">
        <header className="cart-page__header">
          <div><h1>Cart</h1><p>Review availability, pricing, and traveller quantities before checkout.</p></div>
          <div className="cart-page__security"><ShieldCheck /><span>Secure Checkout</span></div>
        </header>

        {loading ? <div className="cart-loading">Refreshing your cart…</div> : items.length === 0 ? (
          <section className="cart-empty"><ShoppingBag /><h2>Your cart is ready for an adventure.</h2><Link to="/">Explore Services</Link></section>
        ) : (
          <div className="cart-layout">
            <section className="cart-groups" aria-label="Cart items">
              {groups.map((group) => {
                const readyIds = group.items.filter((item) => item.status === "Ready").map((item) => item.id);
                const checked = readyIds.length > 0 && readyIds.every((id) => selectedIds.includes(id));
                return (
                  <article className={`cart-group ${group.bundle ? "cart-group--bundle" : ""}`} key={group.key}>
                    {group.bundle && <div className="cart-group__banner"><span>Bundle deal</span><strong>{group.title}</strong></div>}
                    <div className="cart-group__controls">
                      <button type="button" className={`cart-checkbox ${checked ? "is-checked" : ""}`} onClick={() => toggleGroup(group)} aria-label={checked ? "Deselect item" : "Select item"}>{checked && <Check />}</button>
                      <button type="button" className="cart-remove" onClick={() => removeGroup(group)} disabled={busyId === group.key}><Trash2 /> Remove</button>
                    </div>
                    {group.items.map((item) => (
                      <div className="cart-item" key={item.id}>
                        <img src={itemImage(item)} alt="" onError={(event) => { event.currentTarget.src = "/images/Android.png"; }} />
                        <div className="cart-item__copy"><span>{item.serviceType === "thingsToDo" ? "Things To Do" : item.serviceType}</span><h2>{item.itemName}</h2><p>{itemSubtitle(item)}</p>{item.status !== "Ready" && <em>{item.status}</em>}</div>
                        <div className="cart-item__price"><strong>{displayPrice(item)}</strong>{item.quantity > 1 && <span>Quantity {item.quantity}</span>}
                          {item.serviceType === "esim" && !group.bundle && <div className="cart-quantity"><button onClick={() => updateQuantity(item, item.quantity - 1)} disabled={busyId === item.id}><Minus /></button><span>{item.quantity}</span><button onClick={() => updateQuantity(item, item.quantity + 1)} disabled={busyId === item.id}><Plus /></button></div>}
                        </div>
                      </div>
                    ))}
                  </article>
                );
              })}
            </section>
            <aside className="cart-summary"><p className="teleport-eyebrow">Order summary</p><div><span>Selected items</span><strong>{selectedIds.length}</strong></div><div className="cart-summary__total"><span>Estimated total</span><strong>{priceInfo?.displayTotal || "—"}</strong></div><p>Final availability and live prices are confirmed before payment.</p><button onClick={checkout} disabled={!selectedIds.length}>Continue to checkout <ChevronRight /></button></aside>
          </div>
        )}
      </main><Footer /></>
  );
}
